import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const Chart: any;

interface Employee {
  department?: string;
  role?: string;
}

interface DepartmentGroup {
  department: string;
  employees: Employee[];
  color: string;
}

// Fixed categorical order — WA green leads, then warm/cool alternates;
// colorblind-safe on adjacent pairs; never cycled/reassigned by rank.
const CATEGORICAL_COLORS = ['#25D366', '#eb6834', '#128C7E', '#eda100', '#e87ba4', '#2a78d6', '#075E54', '#e34948'];
const OTHER_COLOR = '#8a9e9b';
const MAX_SLICES = 8;
const CHART_SURFACE = '#fcfcfb';
const INK_SECONDARY = '#52514e';

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('departmentCanvas', { static: true }) departmentCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChildren('roleCanvas') roleCanvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;

  isLoading = false;
  errorMessage = '';
  departmentGroups: DepartmentGroup[] = [];
  totalEmployees = 0;

  private charts: any[] = [];
  private roleCanvasSub?: Subscription;
  private viewReady = false;

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    // Kick off the fetch immediately — don't wait on view queries, which only
    // matter for *when* we're able to draw the charts, not for the data itself.
    this.loadEmployees();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.roleCanvasSub = this.roleCanvasRefs.changes.subscribe(() => this.renderRoleCharts());

    // Data may have already arrived before the view was ready (or the reverse) —
    // render immediately if so, instead of relying on load-then-view ordering.
    if (this.departmentGroups.length > 0) {
      this.renderDepartmentChart();
      if (this.roleCanvasRefs.length > 0) {
        this.renderRoleCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.charts.forEach((chart) => chart.destroy());
    this.roleCanvasSub?.unsubscribe();
  }

  trackByDepartment(_index: number, group: DepartmentGroup): string {
    return group.department;
  }

  private loadEmployees() {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall('api/v1/employeemaster/list').subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.buildCharts(res.Data || []);
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load employee data.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load employee data. Please check your connection.';
      },
    });
  }

  private buildCharts(employees: Employee[]) {
    const byDepartment = new Map<string, Employee[]>();
    employees.forEach((emp) => {
      const dept = (emp.department || '').trim() || 'Unassigned';
      if (!byDepartment.has(dept)) {
        byDepartment.set(dept, []);
      }
      byDepartment.get(dept)!.push(emp);
    });

    const sortedDepartments = Array.from(byDepartment.entries()).sort((a, b) => b[1].length - a[1].length);

    this.totalEmployees = employees.length;
    this.departmentGroups = sortedDepartments.map(([department, emps], index) => ({
      department,
      employees: emps,
      color: index < MAX_SLICES - 1 ? CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length] : OTHER_COLOR,
    }));

    if (this.viewReady) {
      this.renderDepartmentChart();
      // Role-split canvases are created by *ngFor after departmentGroups updates;
      // renderRoleCharts() runs off the roleCanvasRefs.changes subscription,
      // which fires once Angular paints the new canvases for this data.
    }
  }

  private renderDepartmentChart() {
    const canvas = this.departmentCanvasRef?.nativeElement;
    if (!canvas || this.departmentGroups.length === 0) {
      return;
    }

    const { labels, data, colors } = this.toSlices(
      this.departmentGroups.map((g) => ({ label: g.department, count: g.employees.length })),
    );

    const chart = new Chart(canvas.getContext('2d'), this.pieConfig(labels, data, colors));
    this.charts.push(chart);
  }

  private renderRoleCharts() {
    this.roleCanvasRefs.forEach((ref, index) => {
      const group = this.departmentGroups[index];
      const canvas = ref?.nativeElement;
      if (!group || !canvas) {
        return;
      }

      const roleCounts = new Map<string, number>();
      group.employees.forEach((emp) => {
        const role = (emp.role || '').trim() || 'Unassigned';
        roleCounts.set(role, (roleCounts.get(role) || 0) + 1);
      });

      const { labels, data, colors } = this.toSlices(
        Array.from(roleCounts.entries()).map(([label, count]) => ({ label, count })),
      );

      const chart = new Chart(canvas.getContext('2d'), this.pieConfig(labels, data, colors));
      this.charts.push(chart);
    });
  }

  /** Shared Chart.js pie config: slice separators, percentage tooltips, circular legend markers. */
  private pieConfig(labels: string[], data: number[], colors: string[]) {
    return {
      type: 'pie',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor: CHART_SURFACE,
            borderWidth: 2,
            hoverBorderWidth: 3,
            hoverBorderColor: CHART_SURFACE,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { animateRotate: true, animateScale: true },
        legend: {
          position: 'right',
          labels: { fontColor: INK_SECONDARY, usePointStyle: true, boxWidth: 10, padding: 14 },
        },
        tooltips: {
          backgroundColor: '#0b0b0bcc',
          titleFontSize: 13,
          bodyFontSize: 13,
          callbacks: {
            label: (item: any, chartData: any) => {
              const dataset = chartData.datasets[item.datasetIndex];
              const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
              const value = dataset.data[item.index];
              const label = chartData.labels[item.index];
              const pct = total ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${pct}%)`;
            },
          },
        },
      },
    };
  }

  /** Sorts by count desc, keeps the top (MAX_SLICES - 1) and folds the remainder into "Other". */
  private toSlices(entries: { label: string; count: number }[]) {
    const sorted = [...entries].sort((a, b) => b.count - a.count);
    const head = sorted.slice(0, MAX_SLICES - 1);
    const tail = sorted.slice(MAX_SLICES - 1);

    const labels = head.map((e) => e.label);
    const data = head.map((e) => e.count);
    const colors = head.map((_, i) => CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length]);

    if (tail.length) {
      labels.push('Other');
      data.push(tail.reduce((sum, e) => sum + e.count, 0));
      colors.push(OTHER_COLOR);
    }

    return { labels, data, colors };
  }
}
